// Receivables (piutang) — Supabase implementation.
//
// Recording a payment is one RPC: it appends to the ledger and moves the
// parent transaction's balance inside a single transaction, so `payments` can
// never disagree with paid_amount/outstanding_amount. The Firestore version
// wrote the two documents separately and hoped both landed.

import { supabase } from '@/lib/supabase';
import { TX_SELECT, mapPayment, mapTransaction } from './mappers';
import type { AppTransaction, PaymentMethod, ReceivablePayment } from '@/types/domain';

export async function listPaymentsByTransaction(
  transactionId: string,
): Promise<ReceivablePayment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('transaction_id', transactionId)
    .order('paid_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapPayment);
}

export async function listPaymentsByCustomer(
  customerId: string,
  max = 100,
): Promise<ReceivablePayment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('customer_id', customerId)
    .order('paid_at', { ascending: false })
    .limit(max);
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapPayment);
}

/**
 * A returned or cancelled invoice keeps its outstanding_amount — the check
 * constraints pin paid + outstanding to grand_total, so the refund cannot be
 * written back without rewriting the invoice itself. Filtering here is what
 * stops those from being chased as debt.
 *
 * 'partially_returned' is deliberately not in this list: the customer kept
 * some of the goods and still owes for them.
 */
const SETTLED_BY_REVERSAL = '(returned,cancelled)';

/** Open receivables for one customer, oldest due first. */
export async function listOutstandingByCustomer(
  customerId: string,
): Promise<AppTransaction[]> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .eq('customer_id', customerId)
    .gt('outstanding_amount', 0)
    .not('payment_status', 'in', SETTLED_BY_REVERSAL)
    .order('due_date', { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapTransaction(r as never));
}

/**
 * Every open receivable, oldest due first — the Piutang screen.
 * Backed by the partial index on (due_date) where outstanding_amount > 0, so
 * this stays cheap as paid invoices accumulate.
 */
export async function listAllOutstanding(max = 500): Promise<AppTransaction[]> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .gt('outstanding_amount', 0)
    .eq('payment_terms', 'tempo')
    .not('payment_status', 'in', SETTLED_BY_REVERSAL)
    .order('due_date', { ascending: true, nullsFirst: false })
    .limit(max);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapTransaction(r as never));
}

export interface RecordPaymentInput {
  transactionId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  notes?: string;
  /**
   * Accepted but ignored. Who received the money is taken from auth.uid()
   * server-side — for a cash ledger, the recorded receiver must be the
   * authenticated user, not whoever the client claims.
   */
  receivedBy?: string;
  receivedByName?: string;
}

export interface RecordPaymentResult {
  outstandingAmount: number;
  paidAmount: number;
  isSettled: boolean;
}

export async function recordPayment(
  input: RecordPaymentInput,
): Promise<RecordPaymentResult> {
  const { data, error } = await supabase.rpc('record_payment', {
    p_transaction_id: input.transactionId,
    p_amount: Math.round(input.amount),
    p_payment_method: input.paymentMethod,
    p_notes: input.notes ?? undefined,
  });
  if (error) throw new Error(error.message);
  const row = data as unknown as { paid_amount: number; outstanding_amount: number };
  return {
    paidAmount: row.paid_amount,
    outstandingAmount: row.outstanding_amount,
    isSettled: row.outstanding_amount <= 0,
  };
}
